import type {CoreLogger, CreateExtArgs, EnvironmentContext, ExtensionAPI} from '@wix/document-manager-core'
import type {
    CompRef,
    IdMap,
    IdMapValue,
    ImageGenerationServerlessResponseWithIdMap,
    ImageGenerationServerlessResponseWithIdMapAndOldUrlMap,
    ImageUrls,
    OldUrlMap
} from '@wix/document-services-types'
import {getNextIdForPrefix, reportBIError, reportImageUploadError} from '../aiExtensionContent'
import {
    aiImageInjectionValidationErrorMessage,
    aiImageInjectionValidationErrorType,
    aiImageUnexpectedError,
    aiImageUnexpectedErrorMessage,
    aiImageUploadErrorMessage,
    aiImageUploadErrorType,
    UPLOAD_STATUS
} from '../constants'
import {getReportableFromError} from '@wix/document-manager-utils'
import {uploadImage} from '../aiExtensionUtils'
import type {SchemaExtensionAPI} from '../../schema/schema'
import {DATA_TYPES} from '../../../constants/constants'
import {injectData} from '../aiBuilderUtils'
import type {ManifestExtensionAPI} from '../../builderComponents/manifest'
import type {InnerElementsExtensionAPI} from '../../builderComponents/innerElements'
import type {ViewerExtensionAPI} from '../../viewer/viewerExtension'
import {merge, mapValues} from 'lodash'

interface ValidationResult {
    isValid: boolean
    reason: string
}

interface ImagePropsInfo {
    idMapValue: IdMapValue
    imageUri: string
    width: number
    height: number
}

type ImageMeasurements = Record<string, {width: number; height: number}>

interface ImagesMap {
    idMap: IdMap
    oldUrlMap: OldUrlMap
    imageMeasurements: ImageMeasurements
}

export const uploadImages = async (
    serverFacade: any,
    logger: CoreLogger,
    images: ImageUrls,
    idMap: IdMap,
    oldUrlMap: OldUrlMap
) => {
    const staticImageUrls: Record<string, string> = {}
    await Promise.all(
        Object.entries(images).map(async ([id, url]) => {
            const {operationStatus, url: staticUrl, error} = (await uploadImage(logger, serverFacade, url)) ?? {}
            if (operationStatus === UPLOAD_STATUS.READY) staticImageUrls[id] = staticUrl
            else {
                staticImageUrls[id] = oldUrlMap[id]
                reportImageUploadError(logger, aiImageUploadErrorType, aiImageUploadErrorMessage, {
                    imageId: idMap[id],
                    tempUrl: url,
                    error
                })
            }
        })
    )
    return staticImageUrls
}

export const injectImages = (
    createExtArgs: CreateExtArgs,
    {data: images, idMap}: ImageGenerationServerlessResponseWithIdMap
) => {
    const {schemaAPI} = createExtArgs.extensionAPI as SchemaExtensionAPI

    Object.entries(images).forEach(([id, url]) => {
        const {pointer, prop, path} = idMap[id] ?? {}
        if (!pointer) return
        const dataToUpdate = () => {
            const imageData = schemaAPI.createItemAccordingToSchema('Builder.Image', DATA_TYPES.data)
            return {data: {[prop]: merge({uri: url}, imageData)}}
        }
        injectData(createExtArgs, pointer, path, dataToUpdate())
    })
}

export const uploadAndInjectImages = async (
    createExtArgs: CreateExtArgs,
    environmentContext: EnvironmentContext,
    {data: images, idMap, oldUrlMap}: ImageGenerationServerlessResponseWithIdMapAndOldUrlMap
): Promise<void> => {
    const {serverFacade} = environmentContext
    const {coreConfig} = createExtArgs
    const {logger} = coreConfig

    const staticImageUrls: ImageUrls = await uploadImages(serverFacade, logger, images, idMap, oldUrlMap)

    injectImages(createExtArgs, {idMap, data: staticImageUrls})
}

export const validateResult = (images: Record<string, string>, idMap: IdMap): ValidationResult => {
    const missingImagesUrl = Object.keys(idMap).filter(img => !images.hasOwnProperty(img))
    return {
        isValid: missingImagesUrl.length === 0,
        reason: missingImagesUrl.length === 0 ? '' : `failed to generate urls for ${missingImagesUrl}`
    }
}

export const processGeneratedImages = async (
    createExtArgs: CreateExtArgs,
    serverFacade: any,
    images: ImageUrls,
    idMap: IdMap,
    oldUrlMap: OldUrlMap,
    logger: CoreLogger
) => {
    const validation = validateResult(images, idMap)
    if (!validation.isValid)
        reportBIError(
            logger,
            aiImageInjectionValidationErrorType,
            aiImageInjectionValidationErrorMessage,
            images,
            validation
        )

    await uploadAndInjectImages(createExtArgs, {serverFacade}, {data: images, idMap, oldUrlMap})
}

export const processAIImageError = (error: Error, logger: CoreLogger): Error => {
    const reportedError = getReportableFromError(error, {
        errorType: aiImageUnexpectedError,
        message: aiImageUnexpectedErrorMessage,
        tags: {
            aiImageInjection: true
        }
    })
    logger.captureError(reportedError)
    return reportedError
}

export const getImagePropsInfo = (extensionAPI: ExtensionAPI, componentPointer: CompRef): ImagePropsInfo[] => {
    const {manifest, innerElements, viewer} = extensionAPI as ManifestExtensionAPI &
        InnerElementsExtensionAPI &
        ViewerExtensionAPI
    const imagePropsInfo: ImagePropsInfo[] = []
    const imageProps = manifest.getData(componentPointer, true, [{prop: 'image'}])
    imageProps.forEach(context => {
        const {prop, path, value: data} = context
        const measurements =
            path !== undefined
                ? innerElements.getInnerElementMeasurements(path)
                : viewer.getComponentMeasurements(componentPointer as CompRef)
        imagePropsInfo.push({
            idMapValue: {pointer: componentPointer, prop, path} as IdMapValue,
            imageUri: data?.uri,
            width: measurements.width,
            height: measurements.height
        })
    })
    return imagePropsInfo
}

export const createImagesMap = (extensionAPI: ExtensionAPI, componentPointers: CompRef[]): ImagesMap => {
    const typeCounter: Record<string, number> = {}
    const imageMeasurements: ImageMeasurements = {}
    const idMap: IdMap = {}
    const oldUrlMap: OldUrlMap = {}
    for (const componentPointer of componentPointers) {
        const imagePropsInfo = getImagePropsInfo(extensionAPI, componentPointer)
        for (const imagePropInfo of imagePropsInfo) {
            const mappedId = getNextIdForPrefix('image', typeCounter)
            imageMeasurements[mappedId] = {width: imagePropInfo.width, height: imagePropInfo.height}
            idMap[mappedId] = imagePropInfo.idMapValue
            oldUrlMap[mappedId] = imagePropInfo.imageUri
        }
    }

    return {idMap, oldUrlMap, imageMeasurements}
}

export const generateAndInjectImages = async <TParams>(
    environmentContext: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    componentPointers: CompRef[],
    buildParams: (key: string, url: string, width: number, height: number) => TParams,
    fetcher: (serverFacade: any, msid: string, paramsMap: Record<string, TParams>) => Promise<Record<string, string>>
): Promise<void> => {
    const {serverFacade} = environmentContext
    const {coreConfig, extensionAPI} = createExtArgs
    const {logger} = coreConfig
    const msid = createExtArgs.dal.get(createExtArgs.pointers.rendererModel.getMetaSiteId())

    const {idMap, oldUrlMap, imageMeasurements} = createImagesMap(extensionAPI, componentPointers)

    const paramsMap: Record<string, TParams> = mapValues(oldUrlMap, (url, key) => {
        if (!url) {
            console.log(`Component with id ${idMap[key].pointer.id} has an undefined image property`)
        }
        const {width, height} = imageMeasurements[key]
        return buildParams(key as string, url, width, height)
    })

    try {
        const urls: Record<string, string> = await fetcher(serverFacade, msid, paramsMap)

        await processGeneratedImages(createExtArgs, serverFacade, urls, idMap, oldUrlMap, logger)
    } catch (error: any) {
        throw processAIImageError(error, logger)
    }
}
